import {cn} from '@tempmee/core'
import {type HTMLAttributes, forwardRef} from 'react'

export type CircularProgressColor = 'primary' | 'secondary' | 'error' | 'inherit'

export interface CircularProgressProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'color'> {
  /** `indeterminate` spins continuously; `determinate` draws an arc for `value`. @default 'indeterminate' */
  variant?: 'indeterminate' | 'determinate'
  /** Progress `0`–`100` (determinate only). */
  value?: number
  /** Diameter in pixels. @default 40 */
  size?: number
  /** Ring stroke width (in the 44px MUI viewBox units). @default 3.6 */
  thickness?: number
  /** Ring color. @default 'primary' */
  color?: CircularProgressColor
  /** Show the rounded `value%` in the center (determinate only). */
  label?: boolean
  /**
   * Optional sub-line under the center `value%` (determinate only) — the Figma
   * "Percent Circle" caption, e.g. "1 of 6". Best suited to the larger sizes.
   */
  caption?: React.ReactNode
}

const STROKE: Record<CircularProgressColor, string> = {
  primary: 'stroke-primary',
  secondary: 'stroke-secondary',
  error: 'stroke-error',
  inherit: 'stroke-current',
}

// MUI geometry: a fixed 44-unit viewBox; the circle radius shrinks by the stroke.
const VIEWBOX = 44
const CENTER = VIEWBOX / 2

/**
 * Circular progress — MUI's `CircularProgress`. `indeterminate` (default) is the
 * spinning ring (the v3 loading indicator); `determinate` draws an arc for `value`
 * (0–100) over a light track ring, and can show a center `label` (`value%`) plus an
 * optional `caption` (the Figma "Percent Circle" sub-line). Sizes are pixel
 * diameters (Figma uses 16 / 32 for spinners, 60 / 120 for percent circles).
 */
export const CircularProgress = forwardRef<HTMLSpanElement, CircularProgressProps>(
  (
    {
      variant = 'indeterminate',
      value = 0,
      size = 40,
      thickness = 3.6,
      color = 'primary',
      label = false,
      caption,
      className,
      ...props
    },
    ref,
  ) => {
    const determinate = variant === 'determinate'
    const clamped = Math.min(100, Math.max(0, value))
    const radius = (VIEWBOX - thickness) / 2
    const circumference = 2 * Math.PI * radius

    return (
      <span
        ref={ref}
        role="progressbar"
        tabIndex={-1}
        aria-valuenow={determinate ? Math.round(clamped) : undefined}
        aria-valuemin={determinate ? 0 : undefined}
        aria-valuemax={determinate ? 100 : undefined}
        aria-label={determinate ? undefined : 'Loading'}
        className={cn('relative inline-flex shrink-0', className)}
        style={{width: size, height: size}}
        {...props}
      >
        <svg
          aria-hidden="true"
          viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
          className={cn(
            'size-full',
            !determinate && 'animate-[circular-progress-rotate_1.4s_linear_infinite]',
          )}
        >
          {/* track ring (determinate only) */}
          {determinate && (
            <circle
              cx={CENTER}
              cy={CENTER}
              r={radius}
              fill="none"
              strokeWidth={thickness}
              className="stroke-divider"
            />
          )}
          <circle
            cx={CENTER}
            cy={CENTER}
            r={radius}
            fill="none"
            strokeWidth={thickness}
            strokeLinecap="round"
            className={cn(
              STROKE[color],
              determinate
                ? 'transition-[stroke-dashoffset] duration-300 ease-out'
                : 'animate-[circular-progress-dash_1.4s_ease-in-out_infinite]',
            )}
            style={
              determinate
                ? {
                    strokeDasharray: circumference,
                    strokeDashoffset: circumference * (1 - clamped / 100),
                    transform: 'rotate(-90deg)',
                    transformOrigin: 'center',
                  }
                : undefined
            }
          />
        </svg>
        {determinate && (label || caption != null) && (
          <span className="absolute inset-0 flex flex-col items-center justify-center text-center leading-none">
            {label && (
              <span
                className="font-bold leading-[1.334] text-text-primary"
                style={{fontSize: Math.max(9, Math.round(size * 0.27))}}
              >
                {Math.round(clamped)}%
              </span>
            )}
            {caption != null && (
              <span className="text-xs leading-[1.66] tracking-[0.4px] text-text-secondary">
                {caption}
              </span>
            )}
          </span>
        )}
      </span>
    )
  },
)
CircularProgress.displayName = 'CircularProgress'
